import { z } from "zod";

/**
 * 設定 → 帳票番号と管理番号（#268 1.5b、docs/98 §6、../eigyouryoku/docs/12 §3 決めごと10・§8 決定6）。
 *   種類ごとに 書式（トークンは半角の引用符で囲む 'YYYY' 'YY' 'MM' '9999'（連番の桁）・ほかは文字）・連番を戻す周期・次の番号（出した最大より小さくは戻せない）・
 *   手入力を許す（請求書の発行の確認で番号を打てる。空なら自動）。前のシステムの番号は請求書の「管理番号」（紙に出さない・検索に入る）。
 *   DB の正は document_number_rules／document_number_counters と fn_set_number_rule・fn_format_document_no（formatDocumentNo は同じ決まり＝見本で確かめる）
 */

/** 設定に並べる種類（DB の fn_number_rule_defaults から 注文請書・締め請求書（旧）を除いたもの。画面は門と使う機能で出し分ける） */
export const NUMBERING_DOC_TYPES = ["quote", "invoice", "receipt", "delivery", "acceptance", "order", "payment_notice", "rfq"] as const;
export const NumberingDocType = z.enum(NUMBERING_DOC_TYPES);
export type NumberingDocType = z.infer<typeof NumberingDocType>;
/** 種類の名前（言葉のキーは画面が t()、API が fillLabels） */
export const NUMBERING_DOC_LABEL: Record<NumberingDocType, string> = {
  quote: "見積書",
  invoice: "請求書",
  receipt: "領収書",
  delivery: "{deliveryNote}",
  acceptance: "{acceptance}",
  order: "{order}",
  payment_notice: "{paymentNotice}",
  rfq: "見積依頼書",
};
/** 手入力の番号を発行の確認で打てる種類（1.5b は請求書だけ） */
export const MANUAL_NUMBER_DOC_TYPES: readonly NumberingDocType[] = ["invoice"];

/** 連番を戻す周期 */
export const NumberReset = z.enum(["never", "yearly", "fiscal_yearly", "monthly"]);
export type NumberReset = z.infer<typeof NumberReset>;
export const NUMBER_RESET_LABEL: Record<NumberReset, string> = { never: "なし", yearly: "毎年", fiscal_yearly: "年度ごと", monthly: "毎月" };

/** 書式に足すトークン（画面のボタン） */
export const NUMBER_TOKENS = [
  { token: "'YYYY'", label: "年4桁" },
  { token: "'YY'", label: "年2桁" },
  { token: "'MM'", label: "月" },
  { token: "'9999'", label: "連番" },
] as const;

export const NUMBERING_MSG = {
  title: "帳票番号",
  help: "書式は、引用符で囲んだ記号が日付と連番に置き換わります（'YYYY'＝西暦4桁・'YY'＝下2桁・'MM'＝月・'9999'＝連番（9 の数が桁数））。引用符の外は文字のまま入ります。次の番号は、発行した番号より小さくはできません",
  format: "書式",
  tokens: "記号",
  sample: "見本",
  reset: "連番リセット",
  next: "次の番号",
  allowManual: "手入力",
  allowManualHelp: "オンにすると、発行前の確認で番号を入力できます。空欄なら自動の番号です。同じ番号は使えません",
  lastNo: "最後に発行した番号",
  none: "—",
  nextValue: (sample: string): string => `次 ${sample}`,
  cancel: "取消",
  confirm: "決定",
  saved: (label: string): string => `${label}の帳票番号を保存しました`,
  /* 断り（API の文） */
  formatRequired: "書式を入れてください",
  formatNoSeq: "書式に連番（'9999'）を入れてください",
  formatTooLong: "書式は 40 文字までです",
  formatQuote: "引用符（'）の数が合っていません",
  formatUsed: "同じ書式をほかの帳票が使っています",
  nextTooSmall: (min: number): string => `次の番号は ${min} 以上にしてください（発行した番号と重なります）`,
  manualOff: "この帳票は番号の手入力を許していません（設定 → 帳票番号）",
  manualBad: "番号は 1〜50 文字で入れてください",
  manualDuplicate: (no: string): string => `番号「${no}」はもう使われています`,
  /* 発行前の確認・請求書の画面 */
  manualLabel: "番号",
  manualAuto: (no: string): string => `空欄なら ${no}`,
  managementNo: "管理番号",
  managementNoHelp: "前のシステムの番号など、社内の目印です。印字しません。検索で探せます",
  managementSaved: "管理番号を保存しました",
  /** 請求書の一覧の番号の下 */
  managementShort: (no: string): string => `管理 ${no}`,
  managementTooLong: "管理番号は 50 文字までです",
} as const;

const SEQ = /'9+'/;
/** 書式（DB の CHECK：連番のトークンが要る）。引用符は対になっている */
export const NumberFormat = z
  .string()
  .trim()
  .min(1, NUMBERING_MSG.formatRequired)
  .max(40, NUMBERING_MSG.formatTooLong)
  .refine((s) => (s.match(/'/g) ?? []).length % 2 === 0, NUMBERING_MSG.formatQuote)
  .refine((s) => SEQ.test(s), NUMBERING_MSG.formatNoSeq);

/** 1つの種類（GET /company/numbering の items） */
export const NumberingItem = z.object({
  docType: NumberingDocType,
  format: z.string(),
  reset: NumberReset,
  allowManual: z.boolean(),
  /** 手入力を打てる種類か（MANUAL_NUMBER_DOC_TYPES） */
  manualAvailable: z.boolean(),
  /** 今の期間の次の連番（カウンタ。まだ無ければ 1） */
  next: z.number().int(),
  /** 次の番号の下限（今の期間に出した最大＋1） */
  minNext: z.number().int(),
  /** 次に付く番号（今日の日付で） */
  sample: z.string(),
  /** 最後に発行した番号（その種類のいちばん新しい発行。無ければ null） */
  lastNo: z.string().nullable(),
});
export type NumberingItem = z.infer<typeof NumberingItem>;
export const NumberingList = z.object({ items: z.array(NumberingItem), fiscalStartMonth: z.number().int() });
export type NumberingList = z.infer<typeof NumberingList>;

/** PUT /company/numbering/:docType（管理者）。next を省くと今のカウンタのまま（ぶつかるなら 400） */
export const NumberingSave = z.object({
  format: NumberFormat,
  reset: NumberReset,
  allowManual: z.boolean().default(false),
  next: z.number().int().min(1).max(2_000_000_000).nullable().optional(),
});
export type NumberingSave = z.input<typeof NumberingSave>;

/** PUT /invoices/:id/management-no：管理番号（null・空で外す）。発行のあとでも */
export const ManagementNoSave = z.object({ managementNo: z.string().trim().max(50, NUMBERING_MSG.managementTooLong).nullable() });
export type ManagementNoSave = z.infer<typeof ManagementNoSave>;

/** 発行前の確認の番号（Invoice.numbering）：手入力を許すか・自動のときに付く番号の見本 */
export const InvoiceNumbering = z.object({ manual: z.boolean(), next: z.string() });
export type InvoiceNumbering = z.infer<typeof InvoiceNumbering>;

/**
 * 番号の書式を展開する（DB の fn_format_document_no と同じ決まり）：'9999'＝連番（桁あふれは切らない）・'FYYYY' 'FYY'＝年度・'YYYY' 'YY' 'MM'＝日付。
 * date は YYYY-MM-DD。fiscalStart＝年度の始めの月（既定 4）
 */
export function formatDocumentNo(format: string, seq: number, date: string, fiscalStart = 4): string {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  const fy = m < fiscalStart ? y - 1 : y;
  let s = format;
  const nine = /'(9+)'/.exec(s)?.[1];
  if (nine) s = s.replace(`'${nine}'`, String(seq).padStart(Math.max(nine.length, String(seq).length), "0"));
  return s
    .split("'FYYYY'").join(String(fy))
    .split("'FYY'").join(String(fy % 100).padStart(2, "0"))
    .split("'YYYY'").join(String(y))
    .split("'YY'").join(String(y % 100).padStart(2, "0"))
    .split("'MM'").join(String(m).padStart(2, "0"));
}

/** 書式の見本（DB の test_numbering ① と同じ答え。API のテストで DB の fn_format_document_no とくらべる） */
export const NUMBER_FORMAT_CASES: ReadonlyArray<{ format: string; seq: number; date: string; want: string }> = [
  { format: "'9999'", seq: 1, date: "2027-07-15", want: "0001" },
  { format: "Q-'YY'-'9999'", seq: 42, date: "2027-07-15", want: "Q-27-0042" },
  { format: "INV-'YYYY''MM'-'999'", seq: 7, date: "2027-07-15", want: "INV-202707-007" },
  { format: "'FYY'-'99999'", seq: 1, date: "2027-03-15", want: "26-00001" },
  { format: "I-'9999'", seq: 12345, date: "2026-11-30", want: "I-12345" },
  { format: "見積-'YYYY'-'9999'", seq: 256, date: "2027-07-15", want: "見積-2027-0256" },
];
